import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { complaintCategoryMasterAPI } from "../services/api";
import Swal from "sweetalert2";
import "bootstrap/dist/css/bootstrap.min.css";
import "./RaiseComplaint.css";
import "./AddEditComplaintCategoryMaster.css";
import { getRoutePermissions } from "../utils/auth";
import { useLocation } from "react-router-dom";

const normalizeComplaintCategory = (item) => ({
  compCatgCode:
    item.compCatgCode || item.CompCatgCode || item.COMPCATGCODE || item.COMPONANTCATGCODE || "",
  compCatgId: item.compCatgId || item.CompCatgId || item.COMPCATGID || item.COMPONANTCATGID || "",
  compCatgName:
    item.compCatgName || item.CompCatgName || item.COMPCATGNAME || item.COMPONANTCATGNAME || "",
  isActive: item.isActive ?? item.IsActive ?? true,
});

const AddEditComplaintCategoryMaster = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { compCatgCode } = useParams();
  const isEditMode = !!compCatgCode;

  // Dynamic privilege checks - gets MenuCode from parent route automatically
  // For add/edit pages, use the parent route path (e.g., "/component-categories" from "/component-categories/add")
  const parentPath = location.pathname.split('/').slice(0, -1).join('/') || '/component-categories';
  const routePermissions = getRoutePermissions(parentPath);
  const { canAdd, canEdit } = routePermissions;

  const [category, setCategory] = useState({
    compCatgCode: "",
    compCatgId: "",
    compCatgName: "",
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    // Check privileges
    if (isEditMode && !canEdit) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to edit component categories.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/component-categories");
      });
      return;
    } else if (!isEditMode && !canAdd) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to add component categories.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        navigate("/component-categories");
      });
      return;
    }

    if (isEditMode) {
      loadCategory();
    } else {
      loadNextCategoryCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compCatgCode]);

  const loadNextCategoryCode = async () => {
    try {
      const response = await complaintCategoryMasterAPI.getNextCode();
      if (response && response.success && response.data) {
        setCategory((prev) => ({ ...prev, compCatgCode: response.data }));
      }
    } catch (e) {
      console.error("Error loading next component category code:", e);
      setCategory((prev) => ({ ...prev, compCatgCode: "CC00001" }));
    }
  };

  const loadCategory = async () => {
    try {
      setLoading(true);
      const response = await complaintCategoryMasterAPI.getByCode(compCatgCode);
      if (response && response.success && response.data) {
        setCategory(normalizeComplaintCategory(response.data));
      } else {
        navigate("/component-categories");
      }
    } catch (e) {
      console.error("Error loading component category:", e);
      if (e.response?.status !== 404) {
        Swal.fire("Error", "Failed to load component category details", "error");
      }
      navigate("/component-categories");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setCategory((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!category.compCatgCode?.trim()) {
        Swal.fire("Error", "Component Category Code is required", "error");
      return false;
    }
    if (!category.compCatgId?.trim()) {
        Swal.fire("Error", "Component Category ID is required", "error");
      return false;
    }
    if (!category.compCatgName?.trim()) {
        Swal.fire("Error", "Component Category Name is required", "error");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check privileges before saving
    if (isEditMode && !canEdit) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to edit component categories.",
        icon: "error",
      });
      return;
    } else if (!isEditMode && !canAdd) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to add component categories.",
        icon: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const createdBy = currentUser?.userIdCode || "SYSADMIN001";
      let response;

      if (isEditMode) {
        response = await complaintCategoryMasterAPI.update(
          compCatgCode,
          category,
          createdBy
        );
      } else {
        response = await complaintCategoryMasterAPI.create(category, createdBy);
      }

      if (response && response.success) {
        Swal.fire({
          title: "Success",
          text:
            response.message || "Component category saved successfully",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/component-categories");
        });
      } else {
        Swal.fire(
          "Error",
          response.message || "Failed to save component category",
          "error"
        );
      }
    } catch (e) {
      console.error("Error saving component category:", e);
      Swal.fire(
        "Error",
        e.response?.data?.message ||
          "Failed to save component category. Please try again.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box className="add-edit-product-loading">
        <CircularProgress />
      </Box>
    );
  }

  if ((isEditMode && !canEdit) || (!isEditMode && !canAdd)) {
    return null;
  }

  return (
    <Box>
      <Box className="add-edit-product-header">
        <Breadcrumbs
          aria-label="breadcrumb"
          className="add-edit-product-breadcrumb"
        >
          <Link
            underline="hover"
            color="inherit"
            href="/component-categories"
            onClick={(e) => {
              e.preventDefault();
              navigate("/component-categories");
            }}
            className="breadcrumb-link brdcrmbLstitms"
          >
            Component Category
          </Link>
          <Typography
            color="text.primary"
            className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
          >
            {isEditMode ? "Edit Component Category" : "Add Component Category"}
          </Typography>
        </Breadcrumbs>
        <Box className="add-edit-product-header-right">
          <Button
            className="add-edit-product-back-button"
            onClick={() => navigate("/component-categories")}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          className="add-edit-product-error-alert"
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      <div className="container-fluid add-edit-product-page">
        {/* Basic Information Section */}
        <div className="card-panel mb-3">
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6 col-lg-4" style={{ display: 'none' }}>
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component Category Code <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={category.compCatgCode}
                  required
                  fullWidth
                  disabled={isEditMode}
                  size="small"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component Category ID <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={category.compCatgId}
                  onChange={(e) => handleChange("compCatgId", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter component category ID"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="textfield-wrapper">
                <label className="select2-label pnlLblsze">
                  Component Category Name <span className="required-asterisk">*</span>
                </label>
                <TextField
                  value={category.compCatgName}
                  onChange={(e) => handleChange("compCatgName", e.target.value)}
                  required
                  fullWidth
                  size="small"
                  placeholder="Enter component category name"
                  className="add-edit-product-textfield"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="row mt-4">
          <div className="col-12">
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={() => navigate("/component-categories")}
                className="btn-cancel-product funbtns"
                startIcon={<CancelIcon />}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={20} /> : <SaveIcon />
                }
                className="btn-save-product funbtns"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default AddEditComplaintCategoryMaster;
